import * as React from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '../auth/auth.store';

/**
 * The console's socket connection.
 *
 * One socket for the whole app, opened when someone signs in and closed when
 * they sign out. Rooms are joined server-side from the JWT — this client cannot
 * ask to listen to a society it is not in, which is the property that makes a
 * shared-database socket safe.
 *
 * Everything arriving here is **untrusted display data**. A payload is used to
 * invalidate a query or raise a toast; nothing is rendered from it that has not
 * come back through the API.
 */

const BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '';

let socket: Socket | null = null;
let currentToken: string | null = null;

// The socket is module state, but components need to re-run their subscription
// when it is replaced. A tiny external store is what lets `useSyncExternalStore`
// see that, rather than each hook racing to read a mutable variable.
const listeners = new Set<() => void>();

function publish(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): Socket | null {
  return socket;
}

/** Opens the connection, or swaps the token on an existing one. */
export function connectSocket(token: string): Socket {
  if (socket && currentToken === token) return socket;

  // A rotated access token means a new handshake — Socket.IO authenticates once,
  // at connect time, so reusing the old socket would keep the old identity.
  if (socket) {
    socket.disconnect();
    socket = null;
  }

  currentToken = token;
  socket = io(BASE_URL || window.location.origin, {
    path: '/socket.io',
    auth: { token },
    // Polling stays in the list: some corporate networks and Indian carrier
    // proxies block the WebSocket upgrade, and a console that silently stops
    // updating is worse than one that long-polls.
    transports: ['websocket', 'polling'],
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 15_000,
    withCredentials: true,
  });

  publish();
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
  currentToken = null;
  publish();
}

/** The live socket, or null when signed out. Re-renders when it is replaced. */
export function useSocket(): Socket | null {
  return React.useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * Keeps the socket in step with the session.
 *
 * Called once, from the app shell. Reconnects on a token refresh and closes on
 * sign-out, so a signed-out tab is not left holding a live subscription to a
 * society's visitor traffic.
 */
export function useSocketConnection(): void {
  const accessToken = useAuthStore((state) => state.accessToken);

  React.useEffect(() => {
    if (!accessToken) {
      disconnectSocket();
      return;
    }
    connectSocket(accessToken);
  }, [accessToken]);

  // Closed when the shell unmounts, which is a sign-out or a full navigation
  // away — not on every token refresh.
  React.useEffect(() => disconnectSocket, []);
}

/**
 * Subscribes to one server event for the life of a component.
 *
 * The handler is held in a ref so a caller can pass an inline arrow without
 * re-subscribing on every render — the mistake that turns one listener into
 * forty and fires a toast forty times.
 */
export function useSocketEvent<TPayload = unknown>(
  event: string,
  handler: (payload: TPayload) => void,
): void {
  const active = useSocket();
  const saved = React.useRef(handler);
  saved.current = handler;

  React.useEffect(() => {
    if (!active) return;

    const listener = (payload: unknown): void => saved.current(payload as TPayload);
    active.on(event, listener);
    return () => {
      active.off(event, listener);
    };
  }, [active, event]);
}
